import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errDetail, fmtIST } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CommStatusBadge, commLabel, EmptyState } from "@/components/common";
import { ProductAliasMapper } from "@/components/AdminExtras";
import {
  CalendarDays, MessageSquare, PhoneCall, Webhook, Copy, Plug,
  RefreshCcw, ShieldAlert, CheckCircle2, ChevronsUpDown, Inbox,
  HardDrive, FolderSync, Globe, AlertTriangle, KeyRound, Sparkles,
  IndianRupee, CircleCheck,
} from "lucide-react";

const PROVIDER_META = {
  google_calendar: {
    title: "Google Workspace — Calendar / Meet / Drive",
    icon: CalendarDays,
    desc: "One Google connection powers consultation calendar events with Meet links AND the customer document repository in Google Drive. SalesOS stays the source of truth — manual fallbacks always work.",
  },
  website_leads: {
    title: "Aussie Pathways Website — Lead Intake",
    icon: Globe,
    desc: "Secure webhook for the external Aussie Pathways website. Leads land in the same canonical dedup engine as every other channel. Secured with a static API key; field names map dynamically.",
  },
  openai_chatgpt: {
    title: "OpenAI ChatGPT — AI Insights",
    icon: Sparkles,
    desc: "Powers the AI insights on Customer 360 (customer summary + intent suggestion). Advisory only — AI output never changes verified CRM data. Uses your own OpenAI API key; no customer phone numbers, emails or documents are ever sent to the model.",
  },
  whatsapp_interakt: {
    title: "WhatsApp (Interakt)",
    icon: MessageSquare,
    desc: "Sends booking invitations, confirmations and reminders via approved WhatsApp templates. Until connected, staff share booking links manually.",
  },
  elevenlabs_voice: {
    title: "ElevenLabs Voice Agent",
    icon: PhoneCall,
    desc: "AI voice agent chases customers who haven't booked, within the calling window (10:00–21:30 IST). When off, chases hand over to human follow-up.",
  },
  meta_events: {
    title: "Meta — Lead Intake & Conversions",
    icon: Webhook,
    desc: "Receives Meta/Pabbly lead webhooks into the canonical dedup engine and reports conversion events back to Meta (hashed identifiers only).",
  },
  razorpay: {
    title: "Razorpay — Payment Links",
    icon: IndianRupee,
    desc: "Generate a payment link for a customer's due instalment from Finance. When Razorpay confirms payment on a link SalesOS created, it's recorded and verified automatically — anything that doesn't match a link SalesOS generated is never guessed at; it raises an alert for staff to locate the customer instead.",
  },
};

const copyText = async (text, what) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${what} copied`);
  } catch {
    toast.error("Could not copy — select and copy manually");
  }
};

const ProviderCard = ({ provider, data, onReload, extra, children }) => {
  const meta = PROVIDER_META[provider] || { title: provider, icon: Plug, desc: "" };
  const Icon = meta.icon;
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    const v = {};
    (data.fields || []).forEach((f) => { v[f.key] = f.value || ""; });
    setValues(v);
  }, [data]);

  const configuredCount = (data.fields || []).filter((f) => f.configured).length;
  const requiredCount = (data.fields || []).filter((f) => !/optional/i.test(f.label)).length;

  const statusBadge = data.enabled ? (
    <Badge data-testid={`integration-status-${provider}`} variant="outline" className="bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30 border">
      <CheckCircle2 className="h-3 w-3 mr-1" /> Live
    </Badge>
  ) : configuredCount >= requiredCount && requiredCount > 0 ? (
    <Badge data-testid={`integration-status-${provider}`} variant="outline" className="bg-[hsl(var(--info))]/10 text-[hsl(var(--info))] border-[hsl(var(--info))]/20 border">
      Configured — switched off
    </Badge>
  ) : (
    <Badge data-testid={`integration-status-${provider}`} variant="outline" className="bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30 border">
      Awaiting credentials
    </Badge>
  );

  const toggle = async (enabled) => {
    setToggling(true);
    try {
      await api.put(`/admin/integrations/${provider}`, { enabled });
      toast.success(enabled
        ? `${meta.title} enabled — queued items will start sending`
        : `${meta.title} switched off — manual fallback active`);
      onReload();
    } catch (e) { toast.error(errDetail(e)); } finally { setToggling(false); }
  };

  const save = async (extraConfig = {}) => {
    setSaving(true);
    try {
      await api.put(`/admin/integrations/${provider}`, { config: { ...values, ...extraConfig } });
      toast.success(`${meta.title} configuration saved`);
      onReload();
    } catch (e) { toast.error(errDetail(e)); } finally { setSaving(false); }
  };

  return (
    <Card data-testid={`integration-card-${provider}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-md bg-muted flex items-center justify-center shrink-0">
            <Icon className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <CardTitle className="text-sm font-heading flex items-center gap-2 flex-wrap">
              {meta.title}
              {statusBadge}
              {provider === "google_calendar" && data.google_connected && (
                <Badge variant="outline" className="bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/20 border text-[10px]">
                  Workspace connected
                </Badge>
              )}
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">{meta.desc}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-[11px] uppercase text-muted-foreground font-semibold hidden sm:inline">
              {data.enabled ? "On" : "Off"}
            </span>
            <Switch
              data-testid={`integration-toggle-${provider}`}
              checked={data.enabled}
              disabled={toggling}
              onCheckedChange={toggle}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {extra}
        {(data.fields || []).length > 0 && (
          <div className="grid sm:grid-cols-2 gap-3">
            {data.fields.map((f) => (
              <div key={f.key} className="space-y-1.5">
                <Label className="text-xs flex items-center gap-1.5">
                  {f.label}
                  {f.configured && <CheckCircle2 className="h-3 w-3 text-[hsl(var(--success))]" />}
                </Label>
                <Input
                  data-testid={`integration-field-${f.key.replace(/\./g, "-")}`}
                  value={values[f.key] ?? ""}
                  placeholder={f.configured ? "Configured" : "Not set"}
                  onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                  className="h-8 text-sm font-mono"
                />
              </div>
            ))}
          </div>
        )}
        {children && children({ save, saving })}
        {!children && (data.fields || []).length > 0 && (
          <div className="flex justify-end">
            <Button data-testid={`integration-save-${provider}`} size="sm" disabled={saving} onClick={() => save()}>
              {saving ? "Saving…" : "Save configuration"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/* 2026-09 Voice consolidation — Administration -> Voice & ElevenLabs is now
   the ONE authoritative place for every ElevenLabs credential/setting
   (master switch, dedicated test-send flag, operational controls, webhook
   health, test-call tooling, monitor). This card is deliberately just a
   compact status + link — no editable fields here anymore, so there's a
   single source of truth in the UI rather than two places that could
   silently disagree. */
const ElevenLabsCompactCard = ({ data }) => {
  const navigate = useNavigate();
  const configuredCount = (data.fields || []).filter((f) => f.configured).length;
  const requiredCount = (data.fields || []).filter((f) => !/optional/i.test(f.label)).length;
  const state = data.enabled ? "live" : configuredCount >= requiredCount && requiredCount > 0 ? "configured" : "off";
  const badge = {
    live: <Badge variant="outline" className="bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30 border"><CheckCircle2 className="h-3 w-3 mr-1" /> Live</Badge>,
    configured: <Badge variant="outline" className="bg-[hsl(var(--info))]/10 text-[hsl(var(--info))] border-[hsl(var(--info))]/20 border">Configured — switched off</Badge>,
    off: <Badge variant="outline" className="bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30 border">Off — awaiting credentials</Badge>,
  }[state];
  return (
    <Card data-testid="integration-card-elevenlabs_voice">
      <CardContent className="pt-4 flex items-center gap-3">
        <div className="h-9 w-9 rounded-md bg-muted flex items-center justify-center shrink-0">
          <PhoneCall className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-heading font-medium flex items-center gap-2 flex-wrap">
            ElevenLabs Voice Agent {badge}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">AI voice agent chases customers who haven't booked. All configuration, the dedicated test-call flag, operational controls and webhook health now live on their own page.</p>
        </div>
        <Button size="sm" variant="outline" data-testid="voice-manage-link" onClick={() => navigate("/admin?tab=voice")}>
          Manage Voice &amp; ElevenLabs
        </Button>
      </CardContent>
    </Card>
  );
};

const WEBSITE_INTAKE_MODES = [["single", "One particular product"], ["mixed", "Multiple products / Mixed leads"]];

/* Structured Website lead-intake config — replaces the old raw-JSON
   field-mapping textbox with the same user-friendly pattern Meta/Sheets
   already use: a real product picker, a plain-language mixed/single
   toggle, and the shared ProductAliasMapper for mixed-mode routing.
   Reuses the EXISTING backend config keys unchanged
   (website.field_mapping / website.intake_mode /
   website.product_identifier_field) — no backend rebuild. */
const WebsiteLeadConfig = ({ data, products, save, saving }) => {
  const mapping = data.website_field_mapping || {};
  const [intakeMode, setIntakeMode] = useState(data.website_intake_mode || "single");
  const [productCode, setProductCode] = useState(mapping._default_product_code || "");
  const [identifierField, setIdentifierField] = useState(data.website_product_identifier_field || "");
  const [nameField, setNameField] = useState(mapping.name || "");
  const [phoneField, setPhoneField] = useState(mapping.phone || "");
  const [emailField, setEmailField] = useState(mapping.email || "");

  useEffect(() => {
    const m = data.website_field_mapping || {};
    setIntakeMode(data.website_intake_mode || "single");
    setIdentifierField(data.website_product_identifier_field || "");
    setProductCode(m._default_product_code || "");
    setNameField(m.name || "");
    setPhoneField(m.phone || "");
    setEmailField(m.email || "");
  }, [data]);

  const submit = () => {
    if (intakeMode === "mixed" && !identifierField.trim()) {
      toast.error("Set which incoming field identifies the product first");
      return;
    }
    if (intakeMode === "single" && !productCode) {
      toast.error("Pick a product for single-product mode");
      return;
    }
    const newMapping = { ...mapping };
    const setOrDelete = (key, val) => { if (val.trim()) newMapping[key] = val.trim(); else delete newMapping[key]; };
    setOrDelete("name", nameField); setOrDelete("phone", phoneField); setOrDelete("email", emailField);
    if (intakeMode === "single" && productCode) newMapping._default_product_code = productCode;
    else delete newMapping._default_product_code;
    save({
      "website.field_mapping": newMapping,
      "website.intake_mode": intakeMode,
      "website.product_identifier_field": intakeMode === "mixed" ? identifierField.trim() : null,
    });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label className="text-xs">Lead type</Label>
        <Select value={intakeMode} onValueChange={setIntakeMode}>
          <SelectTrigger data-testid="website-intake-mode" className="h-9 w-72 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{WEBSITE_INTAKE_MODES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {intakeMode === "single" ? (
        <div className="space-y-1.5">
          <Label className="text-xs">Product</Label>
          <Select value={productCode} onValueChange={setProductCode}>
            <SelectTrigger data-testid="website-product-select" className="h-9 w-72 text-xs"><SelectValue placeholder="Pick product" /></SelectTrigger>
            <SelectContent>{products.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      ) : (
        <div className="space-y-1.5">
          <Label className="text-xs">Which incoming field identifies the product?</Label>
          <Input data-testid="website-identifier-field" className="h-9 w-72 text-xs font-mono" value={identifierField}
            onChange={(e) => setIdentifierField(e.target.value)} placeholder="e.g. visa_category" />
          <p className="text-[11px] text-muted-foreground">The raw JSON key the website sends, e.g. <code className="font-mono">visa_category</code>.</p>
        </div>
      )}

      <div className="space-y-2">
        <Label className="text-xs">Field mapping — Name / Email / Phone</Label>
        <p className="text-[11px] text-muted-foreground">Recognised automatically even without this (common aliases like <code className="font-mono">full_name, mobile</code>) — only set these if the website uses different field names.</p>
        <div className="grid sm:grid-cols-3 gap-2">
          <div className="space-y-1"><Label className="text-[11px] text-muted-foreground">Name field</Label>
            <Input data-testid="website-map-name" className="h-8 text-xs font-mono" value={nameField} onChange={(e) => setNameField(e.target.value)} placeholder="name" /></div>
          <div className="space-y-1"><Label className="text-[11px] text-muted-foreground">Email field</Label>
            <Input data-testid="website-map-email" className="h-8 text-xs font-mono" value={emailField} onChange={(e) => setEmailField(e.target.value)} placeholder="email" /></div>
          <div className="space-y-1"><Label className="text-[11px] text-muted-foreground">Phone field</Label>
            <Input data-testid="website-map-phone" className="h-8 text-xs font-mono" value={phoneField} onChange={(e) => setPhoneField(e.target.value)} placeholder="phone" /></div>
        </div>
      </div>

      {intakeMode === "mixed" && <ProductAliasMapper products={products} />}

      <div className="flex justify-end">
        <Button data-testid="integration-save-website_leads" size="sm" disabled={saving} onClick={submit}>
          {saving ? "Saving…" : "Save configuration"}
        </Button>
      </div>
    </div>
  );
};

/* Full, un-truncated diagnostics for one outbox row (2026-09 Interakt
   audit) — the table's own "Last error" cell is intentionally clipped
   (max-w-56 truncate) to keep the list scannable; this is where the
   complete picture lives. last_dispatch_meta / provider_response_status /
   provider_response_body are additive fields the provider adapter now
   records itself (adapters._wa_send_template) — never a credential, and
   any customer phone number is redacted server-side before it's ever
   stored, so it's always safe to render here as plain text. */
const OutboxDetailsDialog = ({ item, open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg">
      <DialogHeader><DialogTitle>Outbox item details</DialogTitle></DialogHeader>
      {item && (
        <div className="space-y-3 text-xs">
          <div className="grid grid-cols-2 gap-2">
            <div><p className="text-muted-foreground">Provider</p><p className="font-medium">{item.provider}</p></div>
            <div><p className="text-muted-foreground">Status</p><p><CommStatusBadge status={item.status} /></p></div>
            <div><p className="text-muted-foreground">Attempts</p><p className="font-medium">{item.attempts}</p></div>
            <div><p className="text-muted-foreground">Created</p><p className="font-medium">{fmtIST(item.created_at)}</p></div>
          </div>
          {item.last_dispatch_meta && (
            <div className="rounded-md border p-2.5 space-y-1">
              <p className="font-medium text-muted-foreground">What SalesOS attempted to send</p>
              <div className="grid grid-cols-2 gap-1.5 font-mono">
                <div className="text-muted-foreground">Endpoint</div><div className="truncate">{item.last_dispatch_meta.endpoint}</div>
                <div className="text-muted-foreground">Template name</div><div>{item.last_dispatch_meta.template_name || "—"}</div>
                <div className="text-muted-foreground">Language/code</div><div>{item.last_dispatch_meta.template_language || "—"}</div>
                <div className="text-muted-foreground">Destination</div><div>{item.last_dispatch_meta.phone_masked || "—"}</div>
              </div>
            </div>
          )}
          <div className="rounded-md border p-2.5 space-y-1.5">
            <p className="font-medium text-muted-foreground">
              Full provider response {item.provider_response_status != null && `(HTTP ${item.provider_response_status})`}
            </p>
            <pre className="whitespace-pre-wrap break-words font-mono bg-muted/40 rounded p-2 max-h-64 overflow-y-auto" data-testid="outbox-detail-provider-response">
              {item.provider_response_body || item.last_error || "No provider response recorded for this item."}
            </pre>
            <p className="text-[10px] text-muted-foreground">Customer phone numbers are redacted server-side before storage. No API key or token is ever recorded here.</p>
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>
);

const OutboxMonitor = () => {
  const [items, setItems] = useState(null);
  const [status, setStatus] = useState("all");
  const [provider, setProvider] = useState("all");
  const [detailsItem, setDetailsItem] = useState(null);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (status !== "all") params.set("status", status);
    if (provider !== "all") params.set("provider", provider);
    api.get(`/admin/outbox?${params.toString()}`).then((r) => setItems(r.data));
  }, [status, provider]);

  useEffect(() => { load(); }, [load]);

  const retry = async (outboxId) => {
    try {
      await api.post(`/admin/outbox/${outboxId}/retry`);
      toast.success("Requeued — will be attempted on the next scheduler pass");
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  return (
    <Card data-testid="outbox-monitor">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <CardTitle className="text-sm font-heading flex items-center gap-2">
            <Inbox className="h-4 w-4" /> Integration outbox
          </CardTitle>
          <span className="text-xs text-muted-foreground">Every automated send is durable, idempotent and retried — failures never corrupt CRM records.</span>
          <div className="ml-auto flex gap-2">
            <Select value={provider} onValueChange={setProvider}>
              <SelectTrigger data-testid="outbox-provider-filter" className="h-8 w-44 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All providers</SelectItem>
                <SelectItem value="whatsapp_interakt">WhatsApp</SelectItem>
                <SelectItem value="google_calendar">Google Calendar</SelectItem>
                <SelectItem value="elevenlabs_voice">Voice agent</SelectItem>
                <SelectItem value="meta_events">Meta events</SelectItem>
              </SelectContent>
            </Select>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="outbox-status-filter" className="h-8 w-44 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="pending">Queued</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="skipped_provider_off">Pending provider</SelectItem>
                <SelectItem value="skipped_test">Test — not sent</SelectItem>
              </SelectContent>
            </Select>
            <Button data-testid="outbox-refresh" variant="outline" size="sm" className="h-8" onClick={load}>
              <RefreshCcw className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {!items ? <Skeleton className="h-24" />
          : items.length === 0 ? (
            <EmptyState icon={Inbox} title="No outbox entries match this filter"
              hint="Entries appear here whenever SalesOS queues a WhatsApp message, calendar sync, voice call or Meta event." testId="outbox-empty" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Communication</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="hidden md:table-cell">Attempts</TableHead>
                  <TableHead className="hidden lg:table-cell">Last error</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((o) => (
                  <TableRow key={o.outbox_id} data-testid={`outbox-row-${o.outbox_id}`}>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtIST(o.created_at)}</TableCell>
                    <TableCell className="text-sm">
                      {commLabel(o)}
                      {o.is_test_record && <span className="ml-2 text-[10px] font-mono text-[hsl(var(--test))]">TEST</span>}
                    </TableCell>
                    <TableCell><CommStatusBadge status={o.status} /></TableCell>
                    <TableCell className="hidden md:table-cell text-xs">{o.attempts}</TableCell>
                    <TableCell className="hidden lg:table-cell text-xs text-muted-foreground max-w-56 truncate">{o.last_error || "—"}</TableCell>
                    <TableCell className="text-right space-x-1.5">
                      <Button data-testid={`outbox-details-${o.outbox_id}`} size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setDetailsItem(o)}>
                        Details
                      </Button>
                      {["failed", "skipped_provider_off", "skipped_test"].includes(o.status) && (
                        <Button data-testid={`outbox-retry-${o.outbox_id}`} size="sm" variant="outline" className="h-7 text-xs" onClick={() => retry(o.outbox_id)}>
                          <RefreshCcw className="h-3 w-3 mr-1" /> Retry
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        <OutboxDetailsDialog item={detailsItem} open={!!detailsItem} onOpenChange={(v) => !v && setDetailsItem(null)} />
      </CardContent>
    </Card>
  );
};

/* ---- Google Drive: connection status + pending fallback queue ---- */
const DriveSection = ({ drive, onReload }) => {
  const [pending, setPending] = useState(null);
  const [open, setOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [retrying, setRetrying] = useState({});
  const [checkingWho, setCheckingWho] = useState(false);

  const checkWhoami = async () => {
    setCheckingWho(true);
    try {
      const { data } = await api.get("/admin/google/whoami");
      toast.success(data.email ? `Connected as ${data.email}` : "Couldn't determine the email — reconnect Google Workspace");
      onReload();
    } catch (e) { toast.error(errDetail(e)); } finally { setCheckingWho(false); }
  };

  const loadPending = useCallback(() => {
    api.get("/admin/drive/pending").then((r) => setPending(r.data)).catch(() => {});
  }, []);

  useEffect(() => { if (open) loadPending(); }, [open, loadPending]);

  const syncAll = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/drive/sync");
      if (data.synced > 0) toast.success(`${data.synced} document(s) synced to Google Drive`);
      if (data.failed > 0) toast.error(`${data.failed} document(s) still pending — ${data.details.find((d) => d.status === "failed")?.reason || "Drive unavailable"}`);
      loadPending(); onReload();
    } catch (e) { toast.error(errDetail(e)); } finally { setSyncing(false); }
  };

  const retryOne = async (docId) => {
    setRetrying((r) => ({ ...r, [docId]: true }));
    try {
      await api.post(`/documents/${docId}/retry-drive`);
      toast.success("Stored in Google Drive");
      loadPending(); onReload();
    } catch (e) { toast.error(errDetail(e)); }
    finally { setRetrying((r) => ({ ...r, [docId]: false })); }
  };

  const scopeBadge = !drive?.connected ? (
    <Badge data-testid="drive-status-badge" variant="outline" className="bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30 border text-[10px]">
      <AlertTriangle className="h-3 w-3 mr-1" /> Drive not connected
    </Badge>
  ) : drive?.scope_granted ? (
    <Badge data-testid="drive-status-badge" variant="outline" className="bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30 border text-[10px]">
      <CheckCircle2 className="h-3 w-3 mr-1" /> Drive upload scope granted
    </Badge>
  ) : (
    <Badge data-testid="drive-status-badge" variant="outline" className="bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30 border text-[10px]">
      <AlertTriangle className="h-3 w-3 mr-1" /> Drive scope missing — reconnect Google
    </Badge>
  );

  return (
    <div className="rounded-md border p-3 space-y-2" data-testid="drive-section">
      <div className="flex items-center gap-2 flex-wrap">
        <HardDrive className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">Google Drive — customer document repository</span>
        {scopeBadge}
      </div>
      {drive?.connected && (
        <p className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap" data-testid="drive-connected-email">
          Connected as {drive?.connected_email
            ? <code className="font-mono bg-muted rounded px-1">{drive.connected_email}</code>
            : <span className="italic">unknown</span>}.
          <Button data-testid="drive-whoami-check" variant="ghost" size="sm" className="h-6 px-2 text-xs" disabled={checkingWho} onClick={checkWhoami}>
            {checkingWho ? "Checking…" : "Check now"}
          </Button>
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        Root folder <code className="font-mono bg-muted rounded px-1">{drive?.root_name || "Aussie Pathways SalesOS"}</code> / Customers / — a folder named
        <code className="font-mono bg-muted rounded px-1 mx-1">Customer ID - Customer Name</code>
        is created automatically on the first upload for each customer. To use an existing folder you already created, paste its share link into <b>Drive Root Folder Link or ID</b> below — that folder is used directly, no search needed.
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        <Button data-testid="drive-pending-toggle" variant="outline" size="sm" className="h-8 text-xs" onClick={() => setOpen((o) => !o)}>
          <Inbox className="h-3.5 w-3.5 mr-1.5" />
          Drive-pending files{typeof drive?.pending_count === "number" ? ` (${drive.pending_count})` : ""}
          <ChevronsUpDown className="h-3 w-3 ml-1.5" />
        </Button>
        {(drive?.pending_count || 0) > 0 && (
          <Button data-testid="drive-sync-all" size="sm" className="h-8 text-xs" disabled={syncing} onClick={syncAll}>
            <FolderSync className="h-3.5 w-3.5 mr-1.5" /> {syncing ? "Syncing…" : "Sync all to Drive"}
          </Button>
        )}
      </div>
      {open && (
        !pending ? <Skeleton className="h-16" /> : pending.items.length === 0 ? (
          <p className="text-xs text-muted-foreground" data-testid="drive-pending-empty">No files waiting — every document binary is in Google Drive.</p>
        ) : (
          <div className="space-y-1.5" data-testid="drive-pending-list">
            {pending.items.map((d) => (
              <div key={d.document_id} data-testid={`drive-pending-${d.document_id}`} className="flex items-center gap-2 rounded-md bg-muted/50 px-2.5 py-2 text-xs flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{d.filename}</div>
                  <div className="text-muted-foreground truncate">
                    {d.customer_id} · {d.customer_name} · {d.category}
                    {d.is_test_record && <span className="ml-1.5 font-mono text-[hsl(var(--test))]">TEST</span>}
                  </div>
                </div>
                <Badge variant="outline" className="text-[9px] shrink-0">{d.drive_status || "pending"}</Badge>
                <Button data-testid={`drive-retry-${d.document_id}`} size="sm" variant="outline" className="h-7 text-xs shrink-0"
                  disabled={!!retrying[d.document_id]} onClick={() => retryOne(d.document_id)}>
                  <RefreshCcw className="h-3 w-3 mr-1" /> {retrying[d.document_id] ? "Retrying…" : "Retry"}
                </Button>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
};

const RazorpayUnmatchedPanel = () => {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => {
    api.get("/finance/razorpay-unmatched").then((r) => setRows(r.data)).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  const resolve = async (entryId) => {
    try {
      await api.post(`/finance/razorpay-unmatched/${entryId}/resolve`);
      toast.success("Marked resolved");
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };
  if (rows === null) return null;
  return (
    <div className="rounded-md border p-3 space-y-2" data-testid="razorpay-unmatched-panel">
      <p className="text-xs font-medium flex items-center gap-1.5">
        <AlertTriangle className="h-3.5 w-3.5 text-[hsl(var(--warning))]" />
        Unresolved Razorpay payments — needs manual matching ({rows.length})
      </p>
      {rows.length === 0 ? (
        <p className="text-[11px] text-muted-foreground">Nothing waiting — every Razorpay payment received matched a link SalesOS generated.</p>
      ) : (
        <div className="space-y-1.5">
          {rows.map((r) => (
            <div key={r.entry_id} className="flex items-center gap-2 flex-wrap text-xs rounded-md bg-muted/60 px-2.5 py-1.5" data-testid={`razorpay-unmatched-${r.entry_id}`}>
              <span className="font-semibold">₹{Number(r.amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
              <span className="text-muted-foreground">{r.method || "unknown method"} · {r.razorpay_payment_id}</span>
              <span className="text-muted-foreground">{r.payer?.name || "unknown payer"}{r.payer?.contact ? ` · ${r.payer.contact}` : ""}{r.payer?.email ? ` · ${r.payer.email}` : ""}</span>
              <span className="text-muted-foreground">{fmtIST(r.created_at)}</span>
              <Button size="sm" variant="ghost" className="h-6 px-2 ml-auto" data-testid={`razorpay-unmatched-resolve-${r.entry_id}`} onClick={() => resolve(r.entry_id)}>
                <CircleCheck className="h-3.5 w-3.5 mr-1" />Mark resolved
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// AI Runtime & Automatic Refresh (2026-09) — dynamic model discovery
// (never a stale hardcoded list), a deterministic "Test model" compatibility
// check before production use, and per-AI-function "Advanced model routing"
// — all for the SAME OpenAI connection/API key (no duplicated provider
// setup anywhere).
// ---------------------------------------------------------------------------
const MODEL_TEST_STEP_LABELS = {
  api_access_and_text_generation: "API access & plain text generation",
  structured_output_generation: "SalesOS structured-output generation",
  usage_telemetry_capture: "Usage / telemetry capture",
};

const ModelTestResult = ({ result }) => {
  if (!result) return null;
  return (
    <div className={`rounded border p-2 space-y-1 text-[11px] ${result.compatible ? "border-emerald-500/40 bg-emerald-500/5" : "border-red-500/40 bg-red-500/5"}`}
      data-testid="model-test-result">
      <p className="font-medium">{result.compatible ? `✓ ${result.model} is compatible` : `✗ ${result.model} failed compatibility testing`}</p>
      {(result.steps || []).map((s) => (
        <p key={s.step} className={s.ok ? "text-muted-foreground" : "text-red-600 dark:text-red-400"}>
          {s.ok ? "✓" : "✗"} {MODEL_TEST_STEP_LABELS[s.step] || s.step}: {s.detail}
        </p>
      ))}
    </div>
  );
};

// 2026-09 P1 round 3 (post-10-September GPT-4o investigation) — the
// approved-model registry's status for one model id, rendered as a small
// badge next to wherever that model is selected (default/routing/
// fallback). Reused everywhere a model's approval state needs showing,
// rather than each call site re-deriving its own label.
const ModelStatusBadge = ({ model, runtimeStatus }) => {
  if (!model) return null;
  const statusFor = (m) => {
    if (runtimeStatus?.default_model?.model === m) return runtimeStatus.default_model;
    for (const s of Object.values(runtimeStatus?.routing || {})) if (s?.model === m) return s;
    for (const s of Object.values(runtimeStatus?.fallback || {})) if (s?.model === m) return s;
    return null;
  };
  const s = statusFor(model);
  if (!s) return <Badge variant="outline" className="text-[10px] text-muted-foreground">not yet checked</Badge>;
  if (s.blocked) return <Badge variant="outline" className="text-[10px] border-red-500/40 text-red-600 dark:text-red-400">blocked (legacy)</Badge>;
  if (s.approved) return <Badge variant="outline" className="text-[10px] border-emerald-500/40 text-emerald-600 dark:text-emerald-400">approved</Badge>;
  if (s.bootstrap_allowed) return <Badge variant="outline" className="text-[10px] border-[hsl(var(--warning))]/40 text-[hsl(var(--warning))]">bootstrap (temporary)</Badge>;
  return <Badge variant="outline" className="text-[10px] border-red-500/40 text-red-600 dark:text-red-400">not approved</Badge>;
};

// The enforcement-state banner + Activate/Deactivate Strict Enforcement
// action. "bootstrap" is the safe default after upgrade (pre-existing
// config still works, grandfathered in); "strict" requires every
// currently-configured model to be genuinely approved and, once active,
// bootstrap allowances stop authorizing anything.
const EnforcementStateBanner = ({ runtimeStatus, onChanged }) => {
  const [busy, setBusy] = useState(false);
  if (!runtimeStatus) return null;
  const isStrict = runtimeStatus.enforcement_state === "strict";
  const activate = async () => {
    setBusy(true);
    try {
      await api.post("/ai/admin/enforcement/activate-strict");
      toast.success("Strict enforcement activated — bootstrap compatibility no longer authorizes any call");
      onChanged();
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };
  const deactivate = async () => {
    setBusy(true);
    try {
      await api.post("/ai/admin/enforcement/deactivate-strict");
      toast.success("Reverted to bootstrap enforcement");
      onChanged();
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };
  return (
    <div className={`rounded-md border p-3 space-y-2 ${isStrict ? "border-emerald-500/40 bg-emerald-500/5" : "border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/5"}`}
      data-testid="ai-enforcement-state-banner">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-xs font-medium flex items-center gap-1.5">
            {isStrict ? <ShieldAlert className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" /> : <AlertTriangle className="h-3.5 w-3.5 text-[hsl(var(--warning))]" />}
            Enforcement: {isStrict ? "Strict" : "Bootstrap"}
          </p>
          <p className="text-[11px] text-muted-foreground max-w-xl">
            {isStrict
              ? "Only explicitly approved models may run. Bootstrap compatibility is disabled — revoking an approval now fails closed immediately, with no grandfather fallback."
              : "Pre-existing configuration (captured once, at upgrade) is temporarily allowed even though it isn't individually approved yet. A legacy/blocked model (e.g. gpt-4o) is never bootstrap-allowed, and a NEW model an admin enters must always be genuinely approved first — bootstrap is a one-time grandfather clause, never a standing exemption."}
          </p>
          {!isStrict && runtimeStatus.bootstrap_allowed_models?.length > 0 && (
            <p className="text-[11px] text-muted-foreground mt-1">Bootstrap-allowed (pre-existing): <span className="font-mono">{runtimeStatus.bootstrap_allowed_models.join(", ")}</span></p>
          )}
        </div>
        {isStrict ? (
          <Button size="sm" variant="outline" disabled={busy} onClick={deactivate} data-testid="ai-deactivate-strict-button">
            Revert to bootstrap
          </Button>
        ) : (
          <Button size="sm" disabled={busy || !runtimeStatus.can_activate_strict} onClick={activate} data-testid="ai-activate-strict-button">
            Activate Strict Enforcement
          </Button>
        )}
      </div>
      {!isStrict && !runtimeStatus.can_activate_strict && runtimeStatus.strict_activation_blockers?.length > 0 && (
        <div className="text-[11px] text-muted-foreground">
          <p className="font-medium">Cannot activate strict yet — approve these first:</p>
          <ul className="list-disc list-inside">
            {runtimeStatus.strict_activation_blockers.map((p) => <li key={p}>{p}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
};

// Revoke confirmation — a real impact warning (which config paths
// currently reference this model), not a generic "are you sure?".
const RevokeModelDialog = ({ model, open, onOpenChange, onConfirmed }) => {
  const [impact, setImpact] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open && model) {
      api.get(`/ai/admin/models/${encodeURIComponent(model)}/usage-impact`).then((r) => setImpact(r.data)).catch(() => setImpact(null));
    } else {
      setImpact(null);
    }
  }, [open, model]);
  const confirm = async () => {
    setBusy(true);
    try {
      await api.post("/ai/admin/models/revoke", { model });
      toast.success(`${model} revoked`);
      onOpenChange(false);
      onConfirmed();
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };
  const inUse = impact && (impact.used_as_default || impact.used_in_routing?.length || impact.used_in_fallback?.length);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="ai-revoke-model-dialog">
        <DialogHeader><DialogTitle>Revoke approval for {model}?</DialogTitle></DialogHeader>
        <div className="space-y-2 text-sm">
          {!impact ? <Skeleton className="h-16" /> : inUse ? (
            <div className="rounded-md border border-red-500/40 bg-red-500/5 p-2.5 space-y-1">
              <p className="text-xs font-medium flex items-center gap-1.5 text-red-600 dark:text-red-400">
                <AlertTriangle className="h-3.5 w-3.5" />This model is currently in use — real calls will start failing the moment you revoke it
              </p>
              <ul className="text-[11px] text-muted-foreground list-disc list-inside">
                {impact.used_as_default && <li>the current default model</li>}
                {impact.used_in_routing?.map((g) => <li key={`r-${g}`}>the routing override for "{g}"</li>)}
                {impact.used_in_fallback?.map((g) => <li key={`f-${g}`}>the fallback for "{g}"</li>)}
              </ul>
              <p className="text-[11px] text-muted-foreground">Reconfigure those to a different approved model first, or proceed and accept the outage until you do.</p>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">This model isn't currently referenced by the default, any routing override, or any fallback — revoking it is safe.</p>
          )}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button size="sm" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="sm" variant="destructive" disabled={busy || !impact} onClick={confirm} data-testid="ai-revoke-model-confirm">
            {busy ? "Revoking…" : "Revoke anyway"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const OpenAiModelSection = ({ aiModel, setAiModel, save, saving }) => {
  const [models, setModels] = useState({ models: [], source: null, error: null });
  const [loadingModels, setLoadingModels] = useState(true);
  const [customMode, setCustomMode] = useState(false);
  const [customModel, setCustomModel] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [routing, setRouting] = useState(null);
  const [routingDraft, setRoutingDraft] = useState({});
  const [fallbackDraft, setFallbackDraft] = useState({});
  const [savingRouting, setSavingRouting] = useState(false);
  const [routingOpen, setRoutingOpen] = useState(false);
  const [approving, setApproving] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);

  const loadModels = () => {
    setLoadingModels(true);
    api.get("/ai/admin/models").then((r) => setModels(r.data)).catch(() => setModels({ models: [], source: null, error: "Failed to load" }))
      .finally(() => setLoadingModels(false));
  };
  const loadRouting = () => {
    api.get("/ai/admin/runtime-config").then((r) => {
      setRouting(r.data);
      setRoutingDraft(r.data.model_routing || {});
      setFallbackDraft(r.data.model_fallback || {});
    }).catch(() => {});
  };
  const reloadAll = () => { loadModels(); loadRouting(); };
  useEffect(() => { loadModels(); loadRouting(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const runtimeStatus = routing?.runtime_status;
  const lastTestPassed = testResult?.compatible === true;

  useEffect(() => {
    // Reflect the currently-saved model into custom-mode if it isn't in
    // the curated discovered list (e.g. an admin-entered exact model ID).
    if (models.models?.length && aiModel && !models.models.some((m) => m.id === aiModel)) {
      setCustomMode(true);
      setCustomModel(aiModel);
    }
  }, [models, aiModel]);

  const effectiveModel = customMode ? customModel.trim() : aiModel;

  const runTest = async () => {
    if (!effectiveModel) { toast.error("Choose or enter a model first"); return; }
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/ai/admin/models/test", { model: effectiveModel });
      setTestResult(data);
      if (data.compatible) toast.success(`${effectiveModel} passed all compatibility checks`);
      else toast.error(`${effectiveModel} failed compatibility testing — see details below`);
    } catch (e) { toast.error(errDetail(e)); } finally { setTesting(false); }
  };

  const saveRouting = async () => {
    setSavingRouting(true);
    try {
      await api.put("/ai/admin/runtime-config", { model_routing: routingDraft, model_fallback: fallbackDraft });
      toast.success("Model routing saved");
      loadRouting();
    } catch (e) { toast.error(errDetail(e)); } finally { setSavingRouting(false); }
  };

  const approveEffectiveModel = async () => {
    if (!effectiveModel) return;
    setApproving(true);
    try {
      await api.post("/ai/admin/models/approve", { model: effectiveModel });
      toast.success(`${effectiveModel} approved — it may now be used for real generation`);
      reloadAll();
    } catch (e) { toast.error(errDetail(e)); } finally { setApproving(false); }
  };
  const effectiveModelStatus = runtimeStatus?.default_model?.model === effectiveModel ? runtimeStatus.default_model
    : Object.values(runtimeStatus?.routing || {}).find((s) => s?.model === effectiveModel)
    || Object.values(runtimeStatus?.fallback || {}).find((s) => s?.model === effectiveModel) || null;
  const canApproveEffective = lastTestPassed && testResult?.model === effectiveModel && !effectiveModelStatus?.approved && !effectiveModelStatus?.blocked;

  return (
    <div className="space-y-3">
      <EnforcementStateBanner runtimeStatus={runtimeStatus} onChanged={reloadAll} />
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5">
            <Label className="text-xs">Default OpenAI model</Label>
            <ModelStatusBadge model={aiModel} runtimeStatus={runtimeStatus} />
          </div>
          {!customMode ? (
            <Select value={aiModel} onValueChange={setAiModel} disabled={loadingModels}>
              <SelectTrigger data-testid="ai-model-select" className="h-8 text-sm"><SelectValue placeholder={loadingModels ? "Loading models…" : "Choose a model"} /></SelectTrigger>
              <SelectContent>
                {models.models?.map((m) => (
                  <SelectItem key={m.id} value={m.id}>{m.id} — {m.tier}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input data-testid="ai-model-custom-input" className="h-8 text-sm font-mono" placeholder="exact OpenAI model ID"
              value={customModel} onChange={(e) => { setCustomModel(e.target.value); setAiModel(e.target.value); }} />
          )}
          <div className="flex items-center gap-2">
            <button type="button" className="text-[11px] text-muted-foreground underline underline-offset-2"
              data-testid="ai-model-custom-toggle"
              onClick={() => { setCustomMode((v) => !v); setTestResult(null); }}>
              {customMode ? "Choose from discovered models instead" : "Enter a custom model ID instead"}
            </button>
            <button type="button" className="text-[11px] text-muted-foreground underline underline-offset-2" onClick={loadModels}>
              Refresh list
            </button>
          </div>
          {models.source === "fallback" && (
            <p className="text-[11px] text-[hsl(var(--warning))]">Could not reach OpenAI's live model list ({models.error}) — showing a small known-safe fallback only.</p>
          )}
          {models.source === "none" && (
            <p className="text-[11px] text-muted-foreground">Add an API key above first to discover compatible models.</p>
          )}
          <p className="text-[11px] text-muted-foreground">Used by any AI function without its own override below. Compatible text/reasoning models are discovered live from your OpenAI account — image, audio, realtime, transcription, moderation and embedding models are never shown.</p>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Test, then Approve, before saving</Label>
          <div className="grid grid-cols-2 gap-1.5">
            <Button size="sm" variant="outline" className="h-8 text-xs" disabled={testing || !effectiveModel}
              onClick={runTest} data-testid="ai-model-test-button">
              {testing ? "Testing…" : `Test "${effectiveModel || "…"}"`}
            </Button>
            <Button size="sm" className="h-8 text-xs" disabled={approving || !canApproveEffective}
              onClick={approveEffectiveModel} data-testid="ai-model-approve-button"
              title={effectiveModelStatus?.approved ? "Already approved" : !lastTestPassed || testResult?.model !== effectiveModel ? "Run Test Model first — a passing result is required" : ""}>
              {approving ? "Approving…" : effectiveModelStatus?.approved ? "Approved ✓" : "Approve"}
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">A model can only be Approved right after it passes Test Model — never assumed approved just because it's listed or was previously configured. Only an approved (or, during bootstrap, pre-existing) model can actually be used for real generation.</p>
          {effectiveModelStatus?.approved && (
            <button type="button" className="text-[11px] text-red-600 dark:text-red-400 underline underline-offset-2"
              onClick={() => setRevokeTarget(effectiveModel)} data-testid="ai-model-revoke-button">
              Revoke approval
            </button>
          )}
        </div>
      </div>
      <ModelTestResult result={testResult} />
      <div className="flex justify-end">
        <Button data-testid="integration-save-openai_chatgpt" size="sm" disabled={saving}
          onClick={() => save({ "ai.model": effectiveModel })}>
          {saving ? "Saving…" : "Save configuration"}
        </Button>
      </div>
      <RevokeModelDialog model={revokeTarget} open={!!revokeTarget}
        onOpenChange={(v) => !v && setRevokeTarget(null)} onConfirmed={reloadAll} />

      <Separator />
      <Collapsible open={routingOpen} onOpenChange={setRoutingOpen}>
        <CollapsibleTrigger asChild>
          <Button variant="outline" size="sm" className="w-full justify-between" data-testid="model-routing-toggle">
            Advanced model routing — use a different model per AI function
            <ChevronsUpDown className="h-3.5 w-3.5" />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-3 space-y-2">
          <p className="text-[11px] text-muted-foreground">
            Each AI function defaults to the model above ("Use default"). Route a cheaper model to structured extraction and a stronger reasoning model to the Sales Mentor if you want — every call still records the real model actually used in AI Usage.
          </p>
          {(routing?.groups || []).map((g) => (
            <div key={g.key} className="grid grid-cols-12 gap-2 items-center">
              <span className="col-span-4 text-xs text-muted-foreground">{g.label}</span>
              <div className="col-span-4 space-y-0.5">
                <Select value={routingDraft[g.key] || "use_default"}
                  onValueChange={(v) => setRoutingDraft((d) => ({ ...d, [g.key]: v }))}>
                  <SelectTrigger className="h-8 text-xs" data-testid={`model-routing-${g.key}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="use_default">Use default</SelectItem>
                    {models.models?.map((m) => <SelectItem key={m.id} value={m.id}>{m.id}</SelectItem>)}
                  </SelectContent>
                </Select>
                {routingDraft[g.key] && routingDraft[g.key] !== "use_default" && (
                  <ModelStatusBadge model={routingDraft[g.key]} runtimeStatus={runtimeStatus} />
                )}
              </div>
              <div className="col-span-4 space-y-0.5">
                <Select value={fallbackDraft[g.key] || "none"}
                  onValueChange={(v) => setFallbackDraft((d) => ({ ...d, [g.key]: v }))}>
                  <SelectTrigger className="h-8 text-xs" data-testid={`model-fallback-${g.key}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No fallback</SelectItem>
                    {models.models?.map((m) => <SelectItem key={m.id} value={m.id}>{m.id}</SelectItem>)}
                  </SelectContent>
                </Select>
                {fallbackDraft[g.key] && fallbackDraft[g.key] !== "none" && (
                  <ModelStatusBadge model={fallbackDraft[g.key]} runtimeStatus={runtimeStatus} />
                )}
              </div>
            </div>
          ))}
          <p className="text-[11px] text-muted-foreground">Saving a routing/fallback override still requires that model to already be approved (or bootstrap-eligible) — this list shows every discovered model regardless of approval state so you can pick, then Test + Approve it above (using "Enter a custom model ID" if it isn't the current default) before saving here.</p>
          <div className="flex justify-end">
            <Button size="sm" disabled={savingRouting} onClick={saveRouting} data-testid="model-routing-save">
              {savingRouting ? "Saving…" : "Save model routing"}
            </Button>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

export const IntegrationsAdmin = () => {
  const [data, setData] = useState(null);
  const [templates, setTemplates] = useState({});
  const [connecting, setConnecting] = useState(false);
  // 2026-09 P1 round 3 (post-10-September GPT-4o investigation) — this
  // used to default to "gpt-4o" both here and below, which is exactly
  // the class of silent-default bug the backend already removed
  // (ai_provider.get_default_model raises rather than assuming gpt-4o).
  // An unconfigured model must show as genuinely empty, never pre-filled
  // with the one model family this whole investigation is about.
  const [aiModel, setAiModel] = useState("");
  const [products, setProducts] = useState([]);
  const [previewProduct, setPreviewProduct] = useState("");
  const [previewLink, setPreviewLink] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/integrations").then((r) => {
      setData(r.data);
      setTemplates(r.data.whatsapp_templates || {});
      setAiModel(r.data.ai_model || "");
    }).catch((e) => toast.error(errDetail(e)));
    api.get("/products").then((r) => {
      const active = (r.data || []).filter((p) => p.active);
      setProducts(active);
      setPreviewProduct((p) => p || active[0]?.code || "");
    }).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const generatePreviewLink = async () => {
    if (!previewProduct) return;
    setGeneratingLink(true);
    setPreviewLink(null);
    try {
      const { data: d } = await api.post("/admin/booking-link-preview", { product_code: previewProduct });
      setPreviewLink(d.link);
    } catch (e) { toast.error(errDetail(e)); }
    finally { setGeneratingLink(false); }
  };

  const connectGoogle = async () => {
    setConnecting(true);
    try {
      const { data: d } = await api.get("/admin/google/connect");
      window.location.assign(d.authorization_url);
    } catch (e) {
      toast.error(errDetail(e));
      setConnecting(false);
    }
  };

  const toggleTestSends = async (v) => {
    try {
      await api.put("/admin/integrations/meta_events", { config: { "integrations.allow_test_sends": v } });
      toast.success(v ? "TEST records may now trigger real sends — use with care" : "TEST sends blocked again (safe default)");
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  if (!data) return <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-32" />)}</div>;

  const byProvider = Object.fromEntries((data.providers || []).map((p) => [p.provider, p]));
  const redirectUri = `${window.location.origin}/api/oauth/google/callback`;
  const loginRedirectUri = `${window.location.origin}/api/auth/google/callback`;

  return (
    <div className="space-y-4" data-testid="integrations-admin">
      <p className="text-xs text-muted-foreground max-w-3xl">
        Every provider sits behind a kill switch with a manual fallback — a provider outage can never block CRM or Booking.
        Secrets are stored server-side and shown masked. TEST records never trigger real communications unless explicitly allowed below.
      </p>

      {/* Google Workspace: Calendar / Meet / Drive */}
      <ProviderCard provider="google_calendar" data={byProvider.google_calendar || {}} onReload={load}
        extra={(
          <div className="space-y-3">
            <div className="rounded-md bg-muted/60 p-3 space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <Button data-testid="google-connect-button" size="sm" disabled={connecting} onClick={connectGoogle}>
                  <Plug className="h-3.5 w-3.5 mr-1.5" /> {byProvider.google_calendar?.google_connected ? "Reconnect Google Workspace" : "Connect Google Workspace"}
                </Button>
                <span className="text-xs text-muted-foreground">Requires Client ID & Secret saved first. Connecting grants Calendar + Meet + Drive file access in one consent.</span>
              </div>
              <div className="flex items-center gap-2 text-xs flex-wrap">
                <span className="text-muted-foreground shrink-0">Authorized redirect URI for this connect button (add in Google Cloud Console):</span>
                <code data-testid="google-redirect-uri" className="font-mono bg-background border rounded px-1.5 py-0.5 truncate max-w-full">{redirectUri}</code>
                <Button variant="ghost" size="sm" className="h-6 px-1.5" data-testid="google-redirect-copy" onClick={() => copyText(redirectUri, "Redirect URI")}>
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">
                OAuth scopes requested: Calendar events, Calendar read, Sheets read/write (required for the employee working-data sheet under Administration → Export) and <code className="font-mono">drive.file</code> (create/manage files this app creates — required for document uploads and backups). If Google was connected before this update, click Reconnect once to grant the newest scopes.
              </p>
              <div className="flex items-center gap-2 text-xs flex-wrap pt-1 border-t">
                <span className="text-muted-foreground shrink-0">This same Client ID/Secret also powers staff "Sign in with Google" on the login page — add this second redirect URI too:</span>
                <code data-testid="google-login-redirect-uri" className="font-mono bg-background border rounded px-1.5 py-0.5 truncate max-w-full">{loginRedirectUri}</code>
                <Button variant="ghost" size="sm" className="h-6 px-1.5" data-testid="google-login-redirect-copy" onClick={() => copyText(loginRedirectUri, "Login redirect URI")}>
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">
                No Emergent involvement anywhere in sign-in — this is a direct OAuth exchange with Google using only the org's own credentials above. Staff can also sign in with an email + password set by an administrator (Administration → Users), no Google account required.
              </p>
            </div>
            <DriveSection drive={data.google_drive} onReload={load} />
          </div>
        )}
      />

      {/* WhatsApp Interakt with template registry */}
      <ProviderCard provider="whatsapp_interakt" data={byProvider.whatsapp_interakt || {}} onReload={load}>
        {({ save, saving }) => (
          <div className="space-y-3">
            <Collapsible>
              <CollapsibleTrigger asChild>
                <Button data-testid="wa-templates-toggle" variant="outline" size="sm" className="w-full justify-between">
                  Approved template registry ({Object.values(templates).filter((t) => t?.name).length}/{Object.keys(templates).length} mapped)
                  <ChevronsUpDown className="h-3.5 w-3.5" />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-3 space-y-2">
                <p className="text-xs text-muted-foreground">Template names must exactly match the templates approved in your Interakt account. Leave blank until approved — sends will queue as failed-with-reason until mapped.</p>
                {Object.entries(templates).map(([kind, tpl]) => (
                  <div key={kind} className="grid grid-cols-12 gap-2 items-center">
                    <span className="col-span-12 sm:col-span-4 text-xs text-muted-foreground">{tpl?.purpose || kind}</span>
                    <Input
                      data-testid={`wa-template-name-${kind}`}
                      className="col-span-8 sm:col-span-5 h-8 text-sm font-mono"
                      placeholder="approved_template_name"
                      value={tpl?.name || ""}
                      onChange={(e) => setTemplates({ ...templates, [kind]: { ...tpl, name: e.target.value } })}
                    />
                    <Input
                      data-testid={`wa-template-lang-${kind}`}
                      className="col-span-4 sm:col-span-3 h-8 text-sm font-mono"
                      placeholder="en"
                      value={tpl?.language || ""}
                      onChange={(e) => setTemplates({ ...templates, [kind]: { ...tpl, language: e.target.value } })}
                    />
                  </div>
                ))}
              </CollapsibleContent>
            </Collapsible>

            <div className="rounded-md bg-muted/60 p-3 space-y-2">
              <p className="text-xs font-medium">Booking link — for the WhatsApp template's link button</p>
              <p className="text-[11px] text-muted-foreground">
                When you register the template in Meta/Interakt with a "Visit Website" (dynamic URL) button, set the button's base URL to this. SalesOS appends a unique, secure link per customer automatically when the message sends — you never need a static link for a specific person.
              </p>
              <div className="flex items-center gap-2 text-xs">
                <code data-testid="booking-link-base-url" className="font-mono bg-background border rounded px-1.5 py-0.5 truncate flex-1">{data.booking_link_base_url}</code>
                <Button variant="ghost" size="sm" className="h-6 px-1.5" data-testid="booking-link-base-copy" onClick={() => copyText(data.booking_link_base_url, "Booking link base URL")}>
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
              <p className="text-xs font-medium pt-1">Try it — generate a sample link</p>
              <div className="flex items-center gap-2 flex-wrap">
                <Select value={previewProduct} onValueChange={setPreviewProduct}>
                  <SelectTrigger data-testid="booking-link-product" className="h-8 text-xs w-56"><SelectValue placeholder="Choose a product" /></SelectTrigger>
                  <SelectContent>{products.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
                <Button size="sm" className="h-8" data-testid="booking-link-generate" disabled={!previewProduct || generatingLink} onClick={generatePreviewLink}>
                  {generatingLink ? "Generating…" : "Generate sample link"}
                </Button>
              </div>
              {previewLink && (
                <div className="flex items-center gap-2 text-xs">
                  <code data-testid="booking-link-result" className="font-mono bg-background border rounded px-1.5 py-0.5 truncate flex-1">{previewLink}</code>
                  <Button variant="ghost" size="sm" className="h-6 px-1.5" data-testid="booking-link-copy" onClick={() => copyText(previewLink, "Booking link")}>
                    <Copy className="h-3 w-3" />
                  </Button>
                  <Button variant="outline" size="sm" className="h-6 px-2 text-[11px]" onClick={() => window.open(previewLink, "_blank")}>Open</Button>
                </div>
              )}
              <p className="text-[11px] text-muted-foreground">Opens the real customer booking page against a dedicated TEST record — safe to click through, book, or cancel.</p>
            </div>

            {/* 2026-09 Step 3A — Upload CV button base URL, for the Skill
                Migration booking-confirmation template's second dynamic-URL
                button. Same append-a-secure-token-per-send model as the
                booking link above, via services.get_or_create_cv_upload_token. */}
            <div className="rounded-md bg-muted/60 p-3 space-y-2">
              <p className="text-xs font-medium">Upload CV link — for the Skill Migration template's second button</p>
              <p className="text-[11px] text-muted-foreground">
                Set this template's second "Visit Website" button base URL to this (button index 1 — Manage Booking stays button index 0). SalesOS appends a secure, per-opportunity CV-upload token automatically. Skill Migration opportunities only (SM_INHOUSE, SM_MARA, and the bare family root).
              </p>
              <div className="flex items-center gap-2 text-xs">
                <code data-testid="cv-upload-link-base-url" className="font-mono bg-background border rounded px-1.5 py-0.5 truncate flex-1">{data.cv_upload_link_base_url}</code>
                <Button variant="ghost" size="sm" className="h-6 px-1.5" data-testid="cv-upload-link-base-copy" onClick={() => copyText(data.cv_upload_link_base_url, "Upload CV link base URL")}>
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
            </div>

            <div className="flex justify-end">
              <Button data-testid="integration-save-whatsapp_interakt" size="sm" disabled={saving}
                onClick={() => save({ "whatsapp.templates": templates })}>
                {saving ? "Saving…" : "Save configuration & templates"}
              </Button>
            </div>
          </div>
        )}
      </ProviderCard>

      {/* ElevenLabs voice — compact status only (2026-09 Voice
          consolidation); credentials, master switch, dedicated test-send
          flag, operational controls, webhook health/URL/Data Collection
          contract reference all live on Administration -> Voice &
          ElevenLabs now, the one authoritative page. */}
      <ElevenLabsCompactCard data={byProvider.elevenlabs_voice || {}} />

      {/* Razorpay — payment links + confirmation webhook */}
      <ProviderCard provider="razorpay" data={byProvider.razorpay || {}} onReload={load}
        extra={(
          <div className="space-y-3">
            <div className="rounded-md bg-muted/60 p-3 space-y-1.5">
              <p className="text-xs font-medium">Payment confirmation webhook (paste into Razorpay Dashboard → Settings → Webhooks)</p>
              <div className="flex items-center gap-2 text-xs">
                <code data-testid="razorpay-webhook-url" className="font-mono bg-background border rounded px-1.5 py-0.5 truncate flex-1">{data.razorpay_webhook_url}</code>
                <Button variant="ghost" size="sm" className="h-6 px-1.5" data-testid="razorpay-webhook-copy" onClick={() => copyText(data.razorpay_webhook_url, "Webhook URL")}>
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Active events: tick <code className="font-mono">payment_link.paid</code>. Razorpay generates a signing secret when you create the webhook there — paste that value into <b>Webhook Secret</b> below (the direction is reversed from other integrations: Razorpay's secret, not one SalesOS generates).
              </p>
            </div>
            <RazorpayUnmatchedPanel />
          </div>
        )}
      />

      {/* Website lead intake webhook */}
      <ProviderCard provider="website_leads" data={byProvider.website_leads || {}} onReload={load}
        extra={(
          <div className="rounded-md bg-muted/60 p-3 space-y-1.5">
            <p className="text-xs font-medium">Website lead webhook (give this to the website developer)</p>
            <div className="flex items-center gap-2 text-xs">
              <code data-testid="website-webhook-url" className="font-mono bg-background border rounded px-1.5 py-0.5 truncate flex-1">{data.website_lead_webhook_url}</code>
              <Button variant="ghost" size="sm" className="h-6 px-1.5" data-testid="website-webhook-copy" onClick={() => copyText(data.website_lead_webhook_url, "Website webhook URL")}>
                <Copy className="h-3 w-3" />
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground">
              POST JSON with header <code className="font-mono">X-API-Key</code> = the key below. Recognised fields out of the box: <code className="font-mono">name, phone, email, product_code, source, campaign, city, external_event_id</code> (plus common aliases like <code className="font-mono">mobile, full_name, utm_campaign, submission_id</code>). Duplicate submissions are idempotent; phone/email dedup runs automatically.
            </p>
          </div>
        )}
      >
        {({ save, saving }) => (
          <div className="space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Button data-testid="website-key-generate" variant="outline" size="sm" className="h-8 text-xs"
                onClick={() => {
                  const bytes = new Uint8Array(24);
                  crypto.getRandomValues(bytes);
                  const key = "apw_" + btoa(String.fromCharCode(...bytes)).replace(/[+/=]/g, "").slice(0, 32);
                  save({ "website.api_key": key });
                  copyText(key, "New website API key (share it with the website developer now — it is shown masked after saving)");
                }}>
                <KeyRound className="h-3.5 w-3.5 mr-1.5" /> Generate new API key
              </Button>
              <span className="text-[11px] text-muted-foreground">Generating replaces the old key immediately — update the website at the same time.</span>
            </div>
            <WebsiteLeadConfig data={data} products={products} save={save} saving={saving} />
          </div>
        )}
      </ProviderCard>

      {/* OpenAI ChatGPT — AI insights */}
      <ProviderCard provider="openai_chatgpt" data={byProvider.openai_chatgpt || {}} onReload={load}
        extra={(
          <div className="rounded-md bg-muted/60 p-3 space-y-1.5">
            <p className="text-xs font-medium">Where it appears</p>
            <p className="text-[11px] text-muted-foreground">
              Customer 360 → <b>AI insights</b> panel: "Generate summary" (CRM facts + AI assessment) and "Suggest intent" (high intent / warm / cold with reasons & objections).
              Get an API key at <code className="font-mono">platform.openai.com</code> → API keys. The key is stored server-side and shown masked.
            </p>
            <p className="text-[11px] text-muted-foreground">
              Every summary factors in the customer's most recent prior AI summary and any staff feedback left on it (👍/👎 + optional correction note, right under the summary on Customer 360) — so the assessment refines over time instead of starting from scratch on every generation. The summary also refreshes automatically whenever the ElevenLabs voice agent reports a transcript summary from a call.
            </p>
          </div>
        )}
      >
        {({ save, saving }) => (
          <OpenAiModelSection aiModel={aiModel} setAiModel={setAiModel} save={save} saving={saving} />
        )}
      </ProviderCard>

      {/* TEST sends guard */}
      <Card data-testid="test-sends-card" className="border-[hsl(var(--warning))]/40">
        <CardContent className="pt-4 flex items-center gap-3">
          <ShieldAlert className="h-4 w-4 text-[hsl(var(--warning))] shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium">Allow TEST records to trigger real sends</p>
            <p className="text-xs text-muted-foreground">Keep this OFF except when deliberately verifying a provider with a safe sandbox number. TEST comms are otherwise marked “Test — not sent”.</p>
          </div>
          <Switch data-testid="allow-test-sends-toggle" checked={!!data.allow_test_sends} onCheckedChange={toggleTestSends} />
        </CardContent>
      </Card>

      <Separator />
      <OutboxMonitor />
    </div>
  );
};
